import { useState, useEffect, useCallback } from 'react';
import { apiUpdateChecklist, apiGetChecklists, apiGetShowChecklists, apiLinkChecklistToShow, apiUnlinkChecklistFromShow, type ChecklistDTO } from '../api';
import { useTranslation, Trans } from 'react-i18next';
import PdfPreview from './PdfPreview';
import { withCardIcon } from '../lib/uiIcons';

/**
 * ChecklistTab — the "Checklist" tab of the Show detail page (Checklists V2-B).
 *
 * A show can carry MANY checklists (join table tour_date_checklists), grouped
 * by category (departure / changeover / soundcheck / loadout / custom). From
 * here you: add a checklist from the band library, remove one from the show,
 * and tick items live (persisted, broadcast over WebSocket).
 *
 * LEGACY: shows that predate V2-B keep a single `tour_dates.checklist_id`. The
 * boot backfill turned those into join rows; the legacy picker below only
 * appears when the show still holds a legacy list that is not linked (belt and
 * braces on top of the server's legacy fallback in the ?showId read path).
 *
 * Roadcase chrome (`rc-*` classes) kept from the P4c pass. UI strings are 100%
 * i18n keys (EN/FR/ES) — no hardcoded copy.
 */
export interface ChecklistTabProps {
  checklistId: number | null;
  checklists: ChecklistDTO[];
  setChecklists: React.Dispatch<React.SetStateAction<ChecklistDTO[]>>;
  assigning: boolean;
  assignChecklist: (id: number | null) => void;
  copyChecklistForShow: () => void;
  checklistPdfDoc: unknown;
  bandId: string | undefined;
  /** The show's numeric id — enables the V2-B multi-list surface. */
  showId?: string | null;
  /** Whether the user can mutate the show's checklist links (editor+). */
  canWrite?: boolean;
  /** Band members, to resolve assignee ids into display names. */
  members?: { id: string; name: string }[];
}

const CATEGORY_ORDER = ['departure', 'changeover', 'soundcheck', 'loadout', 'custom'];

export default function ChecklistTab({
  checklistId,
  checklists,
  setChecklists,
  assigning,
  assignChecklist,
  copyChecklistForShow,
  checklistPdfDoc,
  bandId,
  showId,
  canWrite,
  members,
}: ChecklistTabProps) {
  const { t } = useTranslation();
  // ONE i18n call for the item count, reused by the picker option and by the
  // `.rc-section-title` that labels the list.
  const itemsLabel = (c: ChecklistDTO) => t('checklists.items', { count: (c.items || []).length });
  const memberName = (id?: string | null) => members?.find((m) => String(m.id) === String(id))?.name || '';

  // ---- Show-linked checklists (V2-B join table) ----
  const [showLists, setShowLists] = useState<ChecklistDTO[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadShowLists = useCallback(() => {
    if (!bandId || !showId) return;
    apiGetShowChecklists(bandId, showId)
      .then((rows) => setShowLists(rows.map((c) => ({ ...c, items: (c.items || []).map((it) => ({ ...it, done: !!it.done })) }))))
      .catch((e) => console.error(e));
  }, [bandId, showId]);

  useEffect(() => { loadShowLists(); }, [loadShowLists]);

  const toggleShowItem = async (cl: ChecklistDTO, it: any) => {
    const next = (cl.items || []).map((x: any) => (x.id === it.id ? { ...x, done: !x.done } : x));
    setShowLists((prev) => prev.map((c) => (c.id === cl.id ? { ...cl, items: next } : c)));
    try {
      await apiUpdateChecklist(bandId || '', String(cl.id), { items: next });
      loadShowLists();
    } catch (e: any) {
      alert(t('checklists.updateFailed', { msg: e.message }));
      loadShowLists();
    }
  };

  const linkList = async (cid: string) => {
    if (!bandId || !showId || !cid) return;
    setBusy(true);
    try {
      await apiLinkChecklistToShow(bandId, cid, showId);
      setPickerOpen(false);
      loadShowLists();
    } catch (e: any) {
      alert(t('checklists.updateFailed', { msg: e.message }));
    } finally {
      setBusy(false);
    }
  };

  const unlinkList = async (cid: string) => {
    if (!bandId || !showId) return;
    setBusy(true);
    try {
      await apiUnlinkChecklistFromShow(bandId, cid, showId);
      loadShowLists();
    } catch (e: any) {
      alert(t('checklists.updateFailed', { msg: e.message }));
    } finally {
      setBusy(false);
    }
  };

  // Library candidates for the picker: visible band-scope lists not yet linked.
  const linkedIds = new Set(showLists.map((c) => String(c.id)));
  const pickerCandidates = checklists.filter((c) => (c.scope || 'band') !== 'personal' && !linkedIds.has(String(c.id)));

  // Group the linked lists by category, in the canonical order.
  const groups = CATEGORY_ORDER
    .map((cat) => ({ cat, lists: showLists.filter((c) => (c.category || 'custom') === cat) }))
    .filter((g) => g.lists.length > 0);

  const showLegacy = showId == null || (showLists.length === 0 && checklistId != null);

  return (
    <div className="card" data-checklist-tab style={{ maxWidth: 1000, marginBottom: 20 }}>
      <div className="rc-card-head">
        <div>
          <h3 className="rc-header-title">{withCardIcon('show-checklist:checklist', t('checklists.tabTitle'))}</h3>
          <p className="rc-header-sub">{t('checklists.subtitle')}</p>
        </div>
        {showId != null && canWrite && (
          <div className="rc-header-actions">
            <button
              className="btn btn-small btn-primary"
              disabled={busy || (pickerCandidates.length === 0 && !pickerOpen)}
              onClick={() => setPickerOpen((v) => !v)}
              data-checklist="add"
            >
              {t('checklists.addChecklist')}
            </button>
          </div>
        )}
      </div>

      {pickerOpen && (
        <div className="rc-form-row" data-checklist="picker">
          <div className="form-field" style={{ flex: '1 1 320px', minWidth: 0 }}>
            <label>{t('checklists.pickFromLibrary')}</label>
            <select
              className="form-input"
              value=""
              disabled={busy}
              onChange={(e) => { if (e.target.value) void linkList(e.target.value); }}
            >
              <option value="">{t('checklists.pickPlaceholder')}</option>
              {pickerCandidates.map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.name} {itemsLabel(cl)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showId != null && showLists.length === 0 && (
        <p style={{ color: 'var(--text-dim)' }} data-checklist="empty">
          <Trans i18nKey="checklists.noListsOnShow" components={{ link: <a href={`/band/${bandId}/checklists`} /> }}>
            No checklist linked to this show yet — add one from the <a href={`/band/${bandId}/checklists`}>Checklists page</a> or with the button above.
          </Trans>
        </p>
      )}

      {groups.map(({ cat, lists }) => (
        <div key={cat} data-checklist={`group-${cat}`} style={{ marginTop: 12 }}>
          <h4 className="rc-section-title">{t(`checklists.category.${cat}`)}</h4>
          <div className="rc-list">
            {lists.map((cl) => {
              const done = (cl.items || []).filter((it: any) => it.done).length;
              return (
                <div key={cl.id} className="rc-card" data-checklist="show-list">
                  <div className="rc-card-head" style={{ marginBottom: 4 }}>
                    <div>
                      <strong className="rc-card-name">{cl.name}</strong>
                      <p className="rc-header-sub">{t('checklists.doneCount', { done, total: (cl.items || []).length })}</p>
                    </div>
                    {canWrite && (
                      <div className="rc-header-actions">
                        <button className="btn btn-small btn-secondary" disabled={busy} onClick={() => void unlinkList(String(cl.id))}>
                          {t('checklists.removeFromShow')}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="rc-list" data-checklist="items">
                    {(cl.items || []).map((it: any) => (
                      <div key={it.id} className="rc-card" data-checklist-item style={{ opacity: it.done ? 0.6 : 1 }}>
                        <input type="checkbox" checked={!!it.done} onChange={() => void toggleShowItem(cl, it)} style={{ width: 16, height: 16, flexShrink: 0 }} />
                        <span className="rc-card-body" style={{ overflowWrap: 'anywhere' }}>{it.text || '—'}</span>
                        {it.assignee_id ? (
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>→ {memberName(it.assignee_id) || it.assigned_to || '—'}</span>
                        ) : it.assigned_to ? (
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>→ {it.assigned_to}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {showLegacy && (
        <>
          <div className="rc-form-row" data-checklist="assign">
            <div className="form-field" style={{ flex: '1 1 320px', minWidth: 0 }}>
              <label>{t('checklists.forShow')}</label>
              <select
                className="form-input"
                value={checklistId != null ? String(checklistId) : ''}
                disabled={assigning || checklists.length === 0}
                onChange={(e) => assignChecklist(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">{t('checklists.noChecklist')}</option>
                {checklists.map((cl) => (
                  <option key={cl.id} value={cl.id}>
                    {cl.name} {itemsLabel(cl)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {checklists.length === 0 && (
            <p style={{ color: 'var(--text-dim)' }}>
              <Trans i18nKey="checklists.noChecklistsYet" components={{ link: <a href={`/band/${bandId}/checklists`} /> }}>
                No checklists yet — create one in the <a href={`/band/${bandId}/checklists`}>Checklists page</a>.
              </Trans>
            </p>
          )}
          {checklistId != null && (
            <div data-checklist="run" style={{ marginTop: 8 }}>
              {(() => {
                const cl = checklists.find((c) => String(c.id) === String(checklistId));
                if (!cl) return null;
                const done = (cl.items || []).filter((it: any) => it.done).length;
                const toggleItem = async (it: any) => {
                  const next = (cl.items || []).map((x: any) => (x.id === it.id ? { ...x, done: !x.done } : x));
                  const updated = { ...cl, items: next };
                  setChecklists((prev) => prev.map((c) => (c.id === cl.id ? updated : c)));
                  try {
                    await apiUpdateChecklist(bandId || '', String(cl.id), { items: next });
                    const refreshed = await apiGetChecklists(bandId || '');
                    setChecklists(refreshed);
                  } catch (e: any) {
                    alert(t('checklists.updateFailed', { msg: e.message }));
                  }
                };
                return (
                  <>
                    <div className="rc-card-head" style={{ marginBottom: 0 }}>
                      <div>
                        <strong className="rc-card-name">{cl.name}</strong>
                        <p className="rc-header-sub">{t('checklists.doneCount', { done, total: (cl.items || []).length })}</p>
                      </div>
                      <div className="rc-header-actions">
                        <button
                          className="btn btn-small btn-secondary"
                          disabled={assigning}
                          onClick={copyChecklistForShow}
                          title={t('checklists.copyTitle')}
                        >
                          {t('checklists.copyAsShow')}
                        </button>
                      </div>
                    </div>
                    <h4 className="rc-section-title" style={{ margin: '4px 0 8px' }}>{itemsLabel(cl)}</h4>
                    <div className="rc-list" data-checklist="items">
                      {(cl.items || []).map((it: any) => (
                        <div key={it.id} className="rc-card" data-checklist-item style={{ opacity: it.done ? 0.6 : 1 }}>
                          <input type="checkbox" checked={!!it.done} onChange={() => toggleItem(it)} style={{ width: 16, height: 16, flexShrink: 0 }} />
                          <span className="rc-card-body" style={{ overflowWrap: 'anywhere' }}>{it.text || '—'}</span>
                          {it.assigned_to ? (
                            <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>→ {it.assigned_to}</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                    {checklistPdfDoc && (
                      <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                        <h4 className="rc-section-title" style={{ margin: '0 0 8px' }}>{t('checklists.pdfPreview')}</h4>
                        <PdfPreview docDef={checklistPdfDoc} height={480} />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
